import type { CategoryNode } from "./types";

/**
 * **La cascada de categorías del producto, aplicada al click y no al cargar.**
 *
 * Marcar una categoría marca toda su descendencia **y sus padres hasta la raíz**, todos
 * asignados; desmarcarla desmarca toda su descendencia y deja a los padres como están. Un
 * padre con algún descendiente marcado, sin estar marcado él —sólo pasa con lo que el
 * producto ya traía—, se ve intermedio y **no** queda asignado.
 *
 * No es la cascada de antd (`treeCheckable` sin `treeCheckStrictly`), y no por gusto: esa
 * se aplica también al valor que llega, así que abrir un producto asignado sólo a un padre
 * mostraba marcados todos sus hijos, y guardarlo se los asignaba sin que nadie los
 * eligiera. Acá lo que el producto ya tiene se muestra tal cual.
 */

/** La categoría y toda su descendencia, por id. */
const conDescendencia = (nodos: CategoryNode[], id: number): number[] => {
  const buscar = (lista: CategoryNode[]): CategoryNode | null => {
    for (const n of lista) {
      if (n.categoryId === id) return n;
      const dentro = buscar(n.children);
      if (dentro) return dentro;
    }
    return null;
  };
  const nodo = buscar(nodos);
  if (!nodo) return [id];
  const out: number[] = [];
  const juntar = (n: CategoryNode) => {
    out.push(n.categoryId);
    n.children.forEach(juntar);
  };
  juntar(nodo);
  return out;
};

/** Los padres de `id`, de la raíz hacia abajo, sin `id`. */
const ancestros = (nodos: CategoryNode[], id: number): number[] => {
  const camino = (lista: CategoryNode[], hasta: number[]): number[] | null => {
    for (const n of lista) {
      if (n.categoryId === id) return hasta;
      const dentro = camino(n.children, [...hasta, n.categoryId]);
      if (dentro) return dentro;
    }
    return null;
  };
  return camino(nodos, []) ?? [];
};

/** Los ids en el orden del árbol, de la raíz hacia abajo: así se listan los tags. */
export const enOrdenDelArbol = (
  seleccion: number[],
  nodos: CategoryNode[],
): number[] => {
  const orden = new Map<number, number>();
  const recorrer = (lista: CategoryNode[]) =>
    lista.forEach((n) => {
      orden.set(n.categoryId, orden.size);
      recorrer(n.children);
    });
  recorrer(nodos);
  return [...seleccion].sort(
    (a, b) => (orden.get(a) ?? Infinity) - (orden.get(b) ?? Infinity),
  );
};

/** La selección de un árbol después de marcar o desmarcar `id`. */
export const aplicarClick = (
  seleccion: number[],
  nodos: CategoryNode[],
  id: number,
  marcada: boolean,
): number[] => {
  const tocadas = conDescendencia(nodos, id);
  if (marcada) {
    return enOrdenDelArbol(
      [...new Set([...seleccion, ...ancestros(nodos, id), ...tocadas])],
      nodos,
    );
  }
  const fuera = new Set(tocadas);
  return seleccion.filter((x) => !fuera.has(x));
};

/** Las categorías que se ven intermedias: sin marcar, con algún descendiente marcado. */
export const intermedias = (
  seleccion: number[],
  nodos: CategoryNode[],
): number[] => {
  const elegidas = new Set(seleccion);
  const out: number[] = [];
  /** Devuelve si `n` o alguno de sus descendientes está marcado. */
  const recorrer = (n: CategoryNode): boolean => {
    const algunHijo = n.children
      .map(recorrer)
      .some(Boolean);
    if (algunHijo && !elegidas.has(n.categoryId)) out.push(n.categoryId);
    return algunHijo || elegidas.has(n.categoryId);
  };
  nodos.forEach(recorrer);
  return out;
};
